import { HttpEvent, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { Observable } from "rxjs";
import { API_HEADERS } from "./api-headers";
import { RuntimeConfigService } from "./runtime-config.service";
import { environment } from "../../environments/environment";

/**
 * Injects the {@code X-Preview: true} header on every outgoing request when
 * {@code RuntimeConfigService.previewMode} is {@code true}. The backend then routes the
 * request to the CoreMedia preview endpoint instead of the live endpoint.
 *
 * Activated at runtime via assets/app-config.json: { "previewMode": true }
 */
export function previewInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (!inject(RuntimeConfigService).previewMode) {
    return next(req);
  }

  // Only backend API calls should carry preview context.
  // Skip external assets (e.g. cdn.claas.com) and other third-party hosts.
  if (!isApiRequest(req.url, environment.api.baseUrl)) {
    return next(req);
  }

  const previewReq = req.clone({
    setHeaders: { [API_HEADERS.preview]: "true" },
  });

  return next(previewReq);
}

function isApiRequest(requestUrl: string, apiBaseUrl: string): boolean {
  if (apiBaseUrl !== "") {
    return requestUrl.startsWith(apiBaseUrl);
  }

  // No absolute API host configured (e.g. local dev via proxy.conf) — the API
  // is reached via relative, same-origin paths. Absolute URLs at this point
  // point to a different origin (e.g. cdn.claas.com) and must be excluded.
  return !/^[a-z][a-z\d+.-]*:\/\//i.test(requestUrl);
}
